import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDate } from "@/lib/dates";
import { Paperclip, UploadSimple, DownloadSimple, Eye, PencilSimple, Trash, CircleNotch, FilePdf, FileXls, FileDoc, FileCsv, FileImage } from "@phosphor-icons/react";

const TYPE_ICON = { pdf: FilePdf, xlsx: FileXls, docx: FileDoc, csv: FileCsv, png: FileImage, jpg: FileImage, jpeg: FileImage };
const PREVIEWABLE = ["pdf", "png", "jpg", "jpeg"];
const prettySize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function AttachmentDialog({ prospectId, attachment, categories, onClose, onDone }) {
  const [form, setForm] = useState({
    title: attachment?.title || "", category: attachment?.category || "Other",
    description: attachment?.description || "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!attachment && !file) { toast.error("Choose a file to upload."); return; }
    setBusy(true);
    try {
      if (attachment) {
        await api.patch(`/prospects/${prospectId}/attachments/${attachment.id}`, form);
        toast.success("Attachment updated");
      } else {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", form.title || file.name);
        fd.append("category", form.category);
        fd.append("description", form.description);
        await api.post(`/prospects/${prospectId}/attachments`, fd);
        toast.success("Document uploaded");
      }
      onDone();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="attachment-dialog">
        <DialogHeader><DialogTitle className="font-heading text-[#032852]">
          {attachment ? "Edit Attachment" : "Upload Document"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!attachment && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">File (PDF, DOCX, XLSX, CSV, PNG, JPG · max 20 MB)</Label>
              <Input type="file" accept=".pdf,.docx,.xlsx,.csv,.png,.jpg,.jpeg" className="mt-1 rounded-sm"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setFile(f);
                  if (f && !form.title) setForm((s) => ({ ...s, title: f.name.replace(/\.[^.]+$/, "") }));
                }} data-testid="attachment-file-input" />
            </div>
          )}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Display title</Label>
            <Input value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
              className="mt-1 rounded-sm" data-testid="attachment-title-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm((s) => ({ ...s, category: v }))}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="attachment-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Description (optional)</Label>
            <Textarea value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
              rows={2} className="mt-1 rounded-sm" data-testid="attachment-description-input" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]"
              data-testid="attachment-save-btn">
              {busy ? <CircleNotch size={15} className="animate-spin" /> : attachment ? "Save" : "Upload"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AttachmentsTab({ prospectId }) {
  const [items, setItems] = useState(null);
  const [categories, setCategories] = useState([]);
  const [dialog, setDialog] = useState(null); // {} for new, {attachment} for edit
  const load = useCallback(() => {
    api.get(`/prospects/${prospectId}/attachments`).then((r) => setItems(r.data))
      .catch(() => toast.error("Attachments could not be loaded."));
  }, [prospectId]);
  useEffect(() => {
    load();
    api.get("/reference/attachment-categories").then((r) => setCategories(r.data)).catch(() => {});
  }, [load]);

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/prospects/${prospectId}/attachments/${a.id}`);
      toast.success("Attachment deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const fileUrl = (a, inline) =>
    `${api.defaults.baseURL}/prospects/${prospectId}/attachments/${a.id}/download${inline ? "?inline=1" : ""}`;

  return (
    <div data-testid="attachments-tab">
      <div className="mb-3 flex justify-end">
        <Button onClick={() => setDialog({})} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]"
          data-testid="upload-attachment-btn">
          <UploadSimple size={16} weight="bold" /> Upload Document
        </Button>
      </div>
      {items === null ? (
        <div className="flex justify-center py-10"><CircleNotch size={22} className="animate-spin text-[#032852]" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-sm border border-dashed border-[#E4E4E7] bg-white py-12 text-center" data-testid="attachments-empty">
          <Paperclip size={28} className="mx-auto text-[#9CA3AF]" />
          <p className="mt-2 text-sm font-semibold text-[#52525B]">No documents yet</p>
          <p className="mt-1 text-xs text-[#6D6D70]">Upload meeting briefs, RFPs, property information, proposals and pricing support.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((a) => {
            const Icon = TYPE_ICON[a.file_ext] || Paperclip;
            return (
              <div key={a.id} className="flex flex-wrap items-start gap-3 rounded-sm border border-[#E4E4E7] bg-white p-3"
                data-testid={`attachment-row-${a.id}`}>
                <Icon size={26} className="mt-0.5 shrink-0 text-[#032852]" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-[#09090B]">{a.title}</p>
                  <p className="mt-0.5 text-[11px] text-[#6D6D70]">
                    <span className="mr-2 rounded-sm bg-[#032852]/5 px-1.5 py-0.5 font-semibold text-[#032852]">{a.category}</span>
                    {a.file_ext.toUpperCase()} · {prettySize(a.size)} · Uploaded {formatDate(a.created_at)} by {a.uploaded_by_name}
                  </p>
                  {a.description && <p className="mt-1 whitespace-pre-wrap text-xs text-[#52525B]">{a.description}</p>}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {PREVIEWABLE.includes(a.file_ext) && (
                    <a href={fileUrl(a, true)} target="_blank" rel="noreferrer" title="Preview"
                      className="rounded-sm p-1.5 text-[#032852] hover:bg-[#032852]/10" data-testid={`preview-attachment-${a.id}`}>
                      <Eye size={16} /></a>
                  )}
                  <a href={fileUrl(a, false)} title="Download"
                    className="rounded-sm p-1.5 text-[#032852] hover:bg-[#032852]/10" data-testid={`download-attachment-${a.id}`}>
                    <DownloadSimple size={16} /></a>
                  <button onClick={() => setDialog({ attachment: a })} title="Edit details"
                    className="rounded-sm p-1.5 text-[#032852] hover:bg-[#032852]/10" data-testid={`edit-attachment-${a.id}`}>
                    <PencilSimple size={16} /></button>
                  <button onClick={() => remove(a)} title="Delete"
                    className="rounded-sm p-1.5 text-[#B91C1C] hover:bg-[#DC2626]/10" data-testid={`delete-attachment-${a.id}`}>
                    <Trash size={16} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {dialog && (
        <AttachmentDialog prospectId={prospectId} attachment={dialog.attachment} categories={categories}
          onClose={() => setDialog(null)} onDone={load} />
      )}
    </div>
  );
}
